import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { Tone, TONE_CLASSES } from "./format";

export interface BreakdownCell {
  value: string;
  tone?: Tone;
}

export interface BreakdownRow {
  key: string;
  label: string;
  cells: BreakdownCell[];
  /** Rows shown under this one when it is expanded. */
  children?: BreakdownRow[];
}

interface BreakdownTableProps {
  header: string;
  columns: string[];
  rows: BreakdownRow[];
  dataTest: string;
}

const gridTemplate = (columnCount: number): React.CSSProperties => ({
  gridTemplateColumns: `24px 172px repeat(${columnCount}, minmax(0, 1fr))`
});

const Cells = ({ cells, isSubRow }: { cells: BreakdownCell[]; isSubRow?: boolean }) => (
  <>
    {cells.map((cell, index) => (
      <Text
        key={index}
        as="div"
        role="cell"
        variant={isSubRow ? "smBodySecondary" : "smBody"}
        className={cn("text-end", TONE_CLASSES[cell.tone ?? "neutral"])}
      >
        {cell.value}
      </Text>
    ))}
  </>
);

/** A label-and-figures table whose rows can carry collapsible sub-rows. */
const BreakdownTable = ({ header, columns, rows, dataTest }: BreakdownTableProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div
      data-test={dataTest}
      role="table"
      className="overflow-hidden rounded-[16px] border-[0.5px] border-border-border1"
    >
      <div
        role="row"
        className="grid h-8 items-center gap-x-2 pr-3"
        style={gridTemplate(columns.length)}
      >
        <div role="cell" />
        <Text
          as="div"
          role={header ? "columnheader" : "cell"}
          variant="smBodySecondary"
        >
          {header}
        </Text>
        {columns.map((column) => (
          <Text
            key={column}
            as="div"
            role="columnheader"
            variant="smBodySecondary"
            className="text-end"
          >
            {column}
          </Text>
        ))}
      </div>
      {rows.map((row) => {
        const isExpanded = expanded.has(row.key);
        return (
          <React.Fragment key={row.key}>
            <div
              role="row"
              data-test={`${dataTest}-row-${row.key}`}
              className="grid h-11 items-center gap-x-2 border-t border-border-border1 pr-3"
              style={gridTemplate(columns.length)}
            >
              <div role="cell" className="flex justify-center">
                {row.children?.length ? (
                  <button
                    type="button"
                    data-test={`${dataTest}-toggle-${row.key}`}
                    aria-expanded={isExpanded}
                    aria-label={t(
                      isExpanded ? "kpisV2.hideDetails" : "kpisV2.showDetails",
                      { label: row.label }
                    )}
                    onClick={() => toggle(row.key)}
                    className="rounded-[6px] hover:bg-surface-hover-strong"
                  >
                    <IconContainer size="sm" color="text-body-secondary">
                      {isExpanded ? <ChevronDown /> : <ChevronRight />}
                    </IconContainer>
                  </button>
                ) : null}
              </div>
              <Text as="div" role="rowheader" variant="smBody" className="truncate">
                {row.label}
              </Text>
              <Cells cells={row.cells} />
            </div>
            {isExpanded &&
              row.children?.map((child) => (
                <div
                  key={child.key}
                  role="row"
                  data-test={`${dataTest}-row-${child.key}`}
                  className="grid h-8 items-center gap-x-2 pr-3"
                  style={gridTemplate(columns.length)}
                >
                  <div role="cell" />
                  <Text
                    as="div"
                    role="rowheader"
                    variant="smBodySecondary"
                    className="truncate pl-2"
                  >
                    {child.label}
                  </Text>
                  <Cells cells={child.cells} isSubRow />
                </div>
              ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default BreakdownTable;
